import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import domain from "../../Services/DomainService";

export default function AtzCrmModal({ open, onClose }) {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [hadError, setHadError] = useState(false);

  const handleClose = () => {
    const result = success ? "success" : hadError ? "error" : null;
    setApiKey("");
    setSuccess(false);
    setError("");
    setHadError(false);
    onClose(result);
  };

  const handleSubmit = async () => {
    if (!apiKey.trim()) {
      setError("Please enter your API token.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(domain() + "atz-crm/save-api-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "JWT " + token,
        },
        body: JSON.stringify({ api_key: apiKey.trim() }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.message || "Failed to save API token. Please check the token and try again.");
        setHadError(true);
      }
    } catch {
      setError("Network error. Please try again.");
      setHadError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, position: "relative" } }}
    >
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{ position: "absolute", top: 10, right: 10, color: "#888" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, px: 4, pb: 2, textAlign: "center" }}>
        {!success && (
          <>
            <Box sx={{ mb: 2 }}>
              <img
                src="/images/atz_crm_logo.jpeg"
                alt="ATZ CRM"
                style={{ height: 56, width: 56, borderRadius: 12, objectFit: "contain" }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 3, fontSize: 18 }}
            >
              Link ATZ CRM Account
            </Typography>
          </>
        )}

        {success ? (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Box sx={{ position: "relative", display: "inline-block", mb: 2 }}>
              <img
                src="/images/atz_crm_logo.jpeg"
                alt="ATZ CRM"
                style={{ height: 56, width: 56, borderRadius: 12, objectFit: "contain", display: "block" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -4,
                  right: -4,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  backgroundColor: "#22c55e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #fff",
                }}
              >
                <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                  <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1.5 }}>
              Success!
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#444", lineHeight: 1.6 }}>
              Kynzo will now automatically track LinkedIn activity from your synced contacts and surface their posts in your Customer Feed, helping you stay engaged and build stronger relationships.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
              API Token <span style={{ color: "#e53e3e" }}>*</span>
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#e53e3e", mb: 1.5 }}>
              The API token for your ATZ CRM account
            </Typography>
            <TextField
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              fullWidth
              variant="outlined"
              error={!!error}
              helperText={error}
              inputProps={{ autoComplete: "off" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f4f4f4",
                  borderRadius: 1.5,
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: "column", px: 4, pb: 3, gap: 1.5, alignItems: "stretch" }}>
        {!success && (
          <>
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                fontFamily: "poppins",
                textTransform: "none",
                backgroundColor: "#2d2d2d",
                borderRadius: 2,
                py: 1.25,
                fontSize: 15,
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#111", boxShadow: "none" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Connect"}
            </Button>
            <Typography sx={{ fontSize: 13, textAlign: "center", color: "#666" }}>
              Need help?{" "}
              <a
                href="https://docs.atzcrm.com/docs/authentication"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#2563EB", textDecoration: "none" }}
              >
                View connection guide ↗
              </a>
            </Typography>
          </>
        )}
        {success && (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              fontFamily: "poppins",
              textTransform: "none",
              backgroundColor: "#111",
              borderRadius: 2,
              py: 1.25,
              fontSize: 15,
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#000", boxShadow: "none" },
            }}
          >
            Finish
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
